import React, { useContext, useEffect, useState } from "react";
import queryString from 'query-string';
import { Link, useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";

import { SystemMessageContext } from "../../../context/SystemMessageContext";
import CampaignCategoriesSelect from "../../../components/CampaignCategoriesSelect/CampaignCategoriesSelect";
import PriorityPicker from "../../../components/PriorityPicker/PriorityPicker";
import Countries from "../../Avatar/AvatarSurvey/Survey/templates/Countries";
import callAPI from "../../../helpers/callAPI";
import ReportTable from "../../../templates/ReportTable/ReportTable";
import Affiliate from "../../../templates/Affiliate/Affiliate";
import Loading from "../../../templates/UI/Loading/Loading";
import Campaign from "../../../templates/Campaign/Campaign";
import numberSort from "../../../helpers/numberSort";
import Number from "../../../templates/Number/Number";
import Dollars from "../../../templates/Dollars/Dollars";


const AffiliatesByCategory = () => {

    document.title = 'Affiliates By Category'

    const location = useLocation()
    const history = useHistory()

    let urlParams = queryString.parse(location.search)

    const [category, setCategory] = useState({
        categoryId: urlParams.categoryId && parseInt(urlParams.categoryId) > 0 ? parseInt(urlParams.categoryId) : 0,
        subCategoryId: urlParams.subCategoryId && parseInt(urlParams.subCategoryId) > 0 ? parseInt(urlParams.subCategoryId) : 0,
    })

    const [country, setCountry] = useState(urlParams.country ? urlParams.country : '')
    const [selectedPriority, setSelectedPriority] = useState(urlParams.priority ? urlParams.priority : '')
    const [incentive, setIncentive] = useState(urlParams.incentive ? (urlParams.incentive === 'true' ? true : false) : false)
    const [network, setNetwork] = useState(urlParams.network ? (urlParams.network === 'true' ? true : false) : false)
    const [trafficNotes, setTrafficNotes] = useState(urlParams.trafficNotes === 'true' ? true : false)
    const [searchResults, setSearchResults] = useState([])
    const [dataLoaded, setDataLoaded] = useState(false)
    const [campaignId, setCampaignId] = useState(urlParams.campaignId && parseInt(urlParams.campaignId) > 0 ? parseInt(urlParams.campaignId) : 0)

    const { showSystemMessage } = useContext(SystemMessageContext);


    useEffect(() => {
        getResults()
    }, [location])

    const handleSubmit = (e) => {
        e.preventDefault()
        let url = buildUrl(`/affiliates/byCategory?z=1`)
        history.push(url)
    }

    const buildUrl = (url) => {
        if (category.categoryId !== 0) {
            url += `&categoryId=${category.categoryId}`
        }

        if (category.subCategoryId !== 0) {
            url += `&subCategoryId=${category.subCategoryId}`
        }

        if (country !== '') {
            url += `&country=${country}`
        }

        if (selectedPriority !== '') {
            url += `&priority=${selectedPriority.code}`
        }

        if (incentive) {
            url += `&incentive=${incentive}`
        }

        if (network) {
            url += `&network=${network}`
        }

        if(campaignId !== 0) {
            url += `&campaignId=${campaignId}`
        }

        return url
    }

    const getResults = () => {
        setDataLoaded(false)

        let url = buildUrl(`affiliates/byCategory?z=1`)

        callAPI(url, 'GET')
            .then((result) => {
                if (result['success'] === true) {
                    setSearchResults(result.affiliates)
                    setDataLoaded(true)
                } else if (result['success'] === false) {
                    showSystemMessage('error', result.errors.message)
                    setDataLoaded(true)
                }
            })
            .catch((error) => {
                showSystemMessage('error', 'An error occurred while getting custom flags')
                setDataLoaded(true)
            })
    }

    const getReportOutput = () => {
        let columns = [
            {
                Header: 'Affiliate ID',
                sortType: numberSort,
                accessor: 'affiliate.id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                }
            },
            {
                Header: 'Affiliate',
                sortType: numberSort,
                accessor: 'affiliate.name',
                Cell: (tableData) => {
                    return (
                        <>
                            <Affiliate aid={tableData.data[tableData.row.index].affiliate.id} name={tableData.data[tableData.row.index].affiliate.name} priority={tableData.data[tableData.row.index].affiliate.priority} status={tableData.data[tableData.row.index].status} />
                        </>
                    )
                }
            },
            {
                Header: 'Campaign',
                accessor: 'offer.name',
                Cell: (tableData) => {
                    return (
                        <>
                            <Campaign oid={tableData.data[tableData.row.index].offer.id} name={tableData.data[tableData.row.index].offer.name} status={tableData.data[tableData.row.index].offer.status.value} />
                        </>
                    )
                }
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Clicks</div>,
                sortType: numberSort,
                accessor: 'stats.clicks',
                className: 'text-right',
                Cell: (tableData) => {
                    return <Link to={buildUrl(`/reports/stats/daily?affiliateId=${tableData.data[tableData.row.index].affiliate.id}`)}>{Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].stats.clicks)}</Link>
                }
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Conversions</div>,
                sortType: numberSort,
                accessor: 'stats.conversions',
                className: 'text-right',
                Cell: (tableData) => {
                    return <Link to={buildUrl(`/reports/stats/conversions?affiliateId=${tableData.data[tableData.row.index].affiliate.id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].stats.conversions)}`}</Link>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>CR %</div>,
                sortType: numberSort,
                accessor: 'stats.conversionRatio',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <>{!isNaN(value) ? <Number value={value} decimals={2} /> : 0}</>
                }

            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Net Earnings (30d)</div>,
                sortType: numberSort,
                accessor: 'stats.networkEarnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>nEPC</div>,
                sortType: numberSort,
                accessor: 'stats.nepc',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div className="pl-3">Affiliate Manager</div>,
                accessor: 'affiliate.manager',
                Cell: ({ cell: { value } }) => {
                    return <span className="pl-3">{value}</span>
                }
            }
        ]

        return (
            <React.Fragment>
                <ReportTable data={searchResults} columns={columns} />
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <div className="page-header d-flex align-items-center">
                <span className="mb-h1">Affiliates By Category (30D stats)</span>
            </div>
            <div className="card">
                <div className="card-body p-0">
                    <div className="d-flex p-3">
                        <form className="d-flex align-items-center" onSubmit={handleSubmit}>
                            {/* <div className="d-flex align-items-center"> */}
                            <span className="d-flex">
                                <CampaignCategoriesSelect category={category} setCategory={setCategory} />
                            </span>

                            {/* <span className="d-flex align-items-center ml-3 text-nowrap">
                                Traffic Notes Only &nbsp;&nbsp;&nbsp;
                                <select name="traffic_notes" className="form-control form-control-sm" defaultValue={trafficNotes} onChange={(e) => setTrafficNotes(e.target.value === 'Y' ? true : false)}>
                                    <option value="">Any</option>
                                    <option value="Y">Yes</option>
                                    <option value="N">No</option>
                                </select>
                            </span> */}

                            <span className="d-flex align-items-center ml-3">
                                Country&nbsp;&nbsp;&nbsp;
                                <select name="country" className="form-control form-control-sm" defaultValue={country} onChange={(e) => setCountry(e.target.value)}>
                                    <option value="">Select Country</option>
                                    <option value="tier1">Tier 1</option>
                                    <Countries />
                                </select>
                            </span>

                            <span className="d-flex ml-3 align-items-center">
                                Priority&nbsp;&nbsp;<PriorityPicker selectedPriority={selectedPriority} setSelectedPriority={setSelectedPriority} className="w-100" />
                            </span>

                            <span className="d-flex align-items-center ml-3 text-nowrap">
                                Incentive Affiliate &nbsp;&nbsp;
                                <select name="incentive" className="form-control form-control-sm" defaultValue={incentive} onChange={(e) => setIncentive(e.target.value === 'Y' ? 'true' : e.target.value === 'N' ? 'false' : '')}>
                                    <option value="">Any</option>
                                    <option value="Y">Yes</option>
                                    <option value="N">No</option>
                                </select>
                            </span>

                            <span className="d-flex align-items-center ml-3 text-nowrap">
                                Network Affiliate&nbsp;&nbsp;
                                <select name="network" className="form-control form-control-sm" defaultValue={network} onChange={(e) => setNetwork(e.target.value === 'Y' ? 'true' : e.target.value === 'N' ? 'false' : '')}>
                                    <option value="">Any</option>
                                    <option value="Y">Yes</option>
                                    <option value="N">No</option>
                                </select>
                            </span>

                            <button type="submit" className="btn btn-sm btn-primary ml-3">
                                Refresh Report
                            </button>
                        </form>
                    </div>
                    {dataLoaded ? searchResults.length === 0 ? <div className="p-3">No stats for the current parameters</div> : getReportOutput() : <Loading />}
                </div>
            </div>
        </React.Fragment>
    )
}

export default AffiliatesByCategory;